import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PageHeader } from '@/components/PageHeader'
import { ask, fetchBrief, generateBrief } from '@/features/intelligence/api'
import type { ApiError } from '@/lib/api/client'
import type { AskAnswer, Brief, BriefFact } from '@/types/intelligence'

/**
 * The daily brief (FR-I1), and the question box beside it (FR-I3).
 *
 * The prose and the figures are shown separately on purpose. The model wrote
 * the sentences; every number underneath was counted in SQL and handed to it
 * (ADR-0007). Showing both is what makes the brief checkable rather than
 * merely fluent — a reader who doubts a sentence can read the figure it came
 * from.
 */
export function BriefPage() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const brief = useQuery<Brief, ApiError>({
    queryKey: ['brief'],
    queryFn: () => fetchBrief(),
    retry: false,
  })

  const write = useMutation<Brief, ApiError, void>({
    mutationFn: generateBrief,
    onSuccess: (written) => queryClient.setQueryData(['brief'], written),
  })

  return (
    <div>
      <PageHeader title={t('brief.title')} subtitle={t('brief.subtitle')} />

      {brief.data === undefined ? (
        <section className="border-line bg-surface mb-9 rounded-md border p-4">
          <p className="text-ink-700 text-sm">{t('brief.none')}</p>
          <button
            type="button"
            onClick={() => write.mutate()}
            disabled={write.isPending}
            className="bg-brand-600 mt-3 rounded px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {write.isPending ? t('brief.generating') : t('brief.generate')}
          </button>
          {write.error === null ? null : (
            <p className="mt-2 text-sm text-[color:var(--color-state-blocked)]">
              {write.error.message}
            </p>
          )}
        </section>
      ) : (
        <>
          <section className="border-line bg-surface mb-6 rounded-md border p-5">
            <div className="text-ink-500 mb-2 font-mono text-[10px] tracking-widest uppercase">
              {brief.data.forDate}
            </div>
            {brief.data.summary === null ? (
              <p className="text-ink-700 text-sm">
                {brief.data.failureReason ?? t('brief.failed')}
              </p>
            ) : (
              <p className="text-ink-900 text-base leading-relaxed whitespace-pre-line">
                {brief.data.summary}
              </p>
            )}
          </section>

          <section className="mb-9">
            <h2 className="text-ink-900 mb-1 text-sm font-semibold">{t('brief.facts')}</h2>
            <p className="text-ink-500 mb-3 max-w-3xl text-xs">{t('brief.computedNote')}</p>
            <FactGrid facts={brief.data.facts} />
          </section>
        </>
      )}

      <AskPanel />
    </div>
  )
}

function FactGrid({ facts }: { facts: BriefFact[] }) {
  return (
    <dl className="border-line bg-line grid grid-cols-2 gap-px border sm:grid-cols-3 lg:grid-cols-4">
      {facts.map((fact) => {
        // A per-record fact repeats its key once per subject, so the tile has
        // to name which one — five tiles reading "Days stalled" with different
        // numbers and no subject is a puzzle, not a brief.
        const subject = typeof fact.meta?.key === 'string' ? fact.meta.key : null

        return (
          <div key={fact.id} className="bg-surface px-4 py-3.5">
            <dd className="text-ink-900 text-2xl font-semibold tabular-nums">
              {Number.isInteger(fact.value) ? fact.value : fact.value.toFixed(1)}
              {fact.unit === null ? null : (
                <span className="text-ink-500 ms-1 text-xs font-normal">{fact.unit}</span>
              )}
            </dd>
            <dt className="text-ink-500 mt-1 text-xs">
              {fact.label}
              {subject === null ? null : (
                <span className="text-brand-600 ms-1.5 font-mono text-[11px]">{subject}</span>
              )}
            </dt>
          </div>
        )
      })}
    </dl>
  )
}

function AskPanel() {
  const { t } = useTranslation()
  const [question, setQuestion] = useState('')

  const answer = useMutation<AskAnswer, ApiError, string>({
    mutationFn: ask,
  })

  return (
    <section>
      <h2 className="text-ink-900 mb-1 text-sm font-semibold">{t('brief.ask')}</h2>
      <p className="text-ink-500 mb-3 max-w-3xl text-xs">{t('brief.askNote')}</p>

      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          if (question.trim().length >= 3) answer.mutate(question.trim())
        }}
      >
        <input
          type="text"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={t('brief.askPlaceholder')}
          className="border-line bg-surface min-w-64 flex-1 rounded border px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={answer.isPending}
          className="bg-brand-600 rounded px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {answer.isPending ? t('brief.asking') : t('brief.ask')}
        </button>
      </form>

      {answer.error === null ? null : (
        <p className="mt-3 text-sm text-[color:var(--color-state-blocked)]">
          {answer.error.message}
        </p>
      )}

      {answer.data === undefined ? null : (
        <div className="mt-4">
          <p className="text-ink-500 mb-2 font-mono text-xs">
            {t('brief.matched', { count: answer.data.matched })}
          </p>
          {answer.data.items.length === 0 ? (
            <p className="border-line bg-surface text-ink-700 rounded-md border p-4 text-sm">
              {t('brief.noAnswer')}
            </p>
          ) : (
            <ul className="border-line bg-surface divide-line divide-y rounded-md border">
              {answer.data.items.map((item) => (
                <li key={item.id} className="flex flex-wrap items-baseline gap-x-3 px-4 py-2.5">
                  <span className="text-brand-600 font-mono text-xs">{item.key}</span>
                  <span className="text-ink-900 min-w-0 flex-1 truncate text-sm">{item.title}</span>
                  <span className="text-ink-500 text-xs">{item.state ?? '—'}</span>
                  <span className="text-ink-500 text-xs">{item.assignee ?? '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}
